import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText, CheckCircle, XCircle, User, Plus, TrendingUp,
  Sparkles, Clock, AlertCircle, Package, ShoppingCart,
  BarChart3, Activity, GripVertical, Star, ClipboardList, ShoppingBag,
  Calendar, Boxes, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Zap, Target, Users, RefreshCw
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area, RadialBarChart, RadialBar
} from 'recharts';
import API_BASE_URL from "../config";

// ─── CUSTOM TOOLTIP ─────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white rounded-xl p-3 shadow-xl text-xs">
      <p className="text-gray-400 mb-2 font-semibold">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 mb-1">
          <div className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-gray-300">{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [prData, setPrData] = useState([]);
  const [poData, setPoData] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [topRatedPRs, setTopRatedPRs] = useState([]);
  const [topRatedPOs, setTopRatedPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingTimeFilter, setRatingTimeFilter] = useState('month');
  const [poRatingTimeFilter, setPoRatingTimeFilter] = useState('month');
  const [draggedItem, setDraggedItem] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser?.userId;
  const isAdmin = currentUser?.positionId === 'SYSTEM ADMIN';
  const allowedInventoryPositions = [
    'Spareparts', 'VP-FINANCE/ADMIN',
    'ASSISTANT PLANT MANAGER', 'Acknowledgement'
  ];
  const canViewInventory = isAdmin || allowedInventoryPositions.includes(currentUser?.positionId);

  const [widgetOrder, setWidgetOrder] = useState(() => {
    const base = ['stats', 'analytics-kpi', 'top-rated-combined', 'charts', 'analytics-advanced', 'combined-section', 'actions'];
    return canViewInventory
      ? ['stats', 'analytics-kpi', 'top-rated-combined', 'charts', 'analytics-advanced', 'combined-section', 'inventory', 'actions']
      : base;
  });

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchData(); }, [ratingTimeFilter, poRatingTimeFilter]);

  const fetchData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.userId;
      const admin = user?.positionId === 'SYSTEM ADMIN';
      const canInv = admin || allowedInventoryPositions.includes(user?.positionId);

      const prResponse = await fetch(`${API_BASE_URL}api/PR_Header`);
      const prResult = await prResponse.json();
      const filteredPRs = admin ? prResult : prResult.filter(pr => pr.createdBy === userId);
      setPrData(filteredPRs);

      const poResponse = await fetch(`${API_BASE_URL}api/T_PO_Header`);
      const poResult = await poResponse.json();
      const filteredPOs = admin ? poResult : poResult.filter(po => po.preparedBy === userId);
      setPoData(filteredPOs);

      if (canInv) {
        const invResponse = await fetch(`${API_BASE_URL}api/InventoryData`);
        if (invResponse.ok) setInventoryData(await invResponse.json());
      }

      const ratedResponse = await fetch(`${API_BASE_URL}api/PR_Header/rated`);
      if (ratedResponse.ok) {
        const ratedData = await ratedResponse.json();
        const enriched = ratedData
          .map(r => { const full = filteredPRs.find(pr => pr.id === r.id); return full ? { ...full, ...r } : null; })
          .filter(Boolean);
        const filtered = filterByTimePeriod(enriched, ratingTimeFilter);
        setTopRatedPRs(filtered.sort((a, b) => b.rate - a.rate).slice(0, 3));
      }

      const ratedPOResponse = await fetch(`${API_BASE_URL}api/T_PO_Header/rated`);
      if (ratedPOResponse.ok) {
        const ratedPOData = await ratedPOResponse.json();
        const enriched = ratedPOData
          .map(r => { const full = filteredPOs.find(po => po.id === r.id); return full ? { ...full, ...r } : null; })
          .filter(Boolean);
        const filtered = filterByTimePeriod(enriched, poRatingTimeFilter, true);
        setTopRatedPOs(filtered.sort((a, b) => b.rate - a.rate).slice(0, 3));
      }

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterByTimePeriod = (data, timeFilter, isPO = false) => {
    const now = new Date();
    return data.filter(item => {
      const d = new Date(isPO ? item.poCreatedDate : item.creationDate);
      const days = (now - d) / (1000 * 60 * 60 * 24);
      return timeFilter === 'day' ? days <= 1 : timeFilter === '3days' ? days <= 3 : timeFilter === 'week' ? days <= 7 : days <= 30;
    });
  };

  // ─── DRAG HANDLERS ──────────────────────────────────────────────────────────
  const handleDragStart = (e, id) => { setDraggedItem(id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;
    const newOrder = [...widgetOrder];
    const from = newOrder.indexOf(draggedItem);
    const to = newOrder.indexOf(targetId);
    newOrder.splice(from, 1);
    newOrder.splice(to, 0, draggedItem);
    setWidgetOrder(newOrder);
    setDraggedItem(null);
  };
  const handleDragEnd = () => setDraggedItem(null);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';

  // ─── STATS ───────────────────────────────────────────────────────────────────
  const prStats = useMemo(() => ({
    draft: prData.filter(p => p.status === 0).length,
    pending: prData.filter(p => p.status === 1).length,
    approved: prData.filter(p => p.status === 2).length,
    rejected: prData.filter(p => p.status === 3).length,
    total: prData.length
  }), [prData]);

  const poStats = useMemo(() => ({
    draft: poData.filter(p => p.status === 0).length,
    pending: poData.filter(p => p.status === 1).length,
    approved: poData.filter(p => p.status === 2).length,
    rejected: poData.filter(p => p.status === 3).length,
    total: poData.length
  }), [poData]);

  // ─── ANALYTICS COMPUTATIONS ─────────────────────────────────────────────────
  const analyticsData = useMemo(() => {
    // PR Approval Rate
    const prApprovalRate = prStats.total > 0
      ? Math.round((prStats.approved / prStats.total) * 100) : 0;
    const poApprovalRate = poStats.total > 0
      ? Math.round((poStats.approved / poStats.total) * 100) : 0;

    // PR to PO conversion rate (approved PRs that have matching POs)
    const conversionRate = prStats.approved > 0
      ? Math.round((poStats.total / prStats.approved) * 100) : 0;

    // Monthly data (last 6 months)
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const monthLabel = d.toLocaleDateString('en-US', { month: 'short' });
      const m = d.getMonth();
      const y = d.getFullYear();
      months.push({
        month: monthLabel,
        PR: prData.filter(p => { const pd = new Date(p.creationDate); return pd.getMonth() === m && pd.getFullYear() === y; }).length,
        PO: poData.filter(p => { const pd = new Date(p.poCreatedDate); return pd.getMonth() === m && pd.getFullYear() === y; }).length,
        Approved: prData.filter(p => { const pd = new Date(p.creationDate); return pd.getMonth() === m && pd.getFullYear() === y && p.status === 2; }).length,
      });
    }

    // Weekly trend (last 4 weeks)
    const weeks = [];
    for (let i = 3; i >= 0; i--) {
      const start = new Date(); start.setDate(start.getDate() - (i + 1) * 7);
      const end = new Date(); end.setDate(end.getDate() - i * 7);
      weeks.push({
        week: `W${4 - i}`,
        PR: prData.filter(p => { const d = new Date(p.creationDate); return d >= start && d < end; }).length,
        PO: poData.filter(p => { const d = new Date(p.poCreatedDate); return d >= start && d < end; }).length,
      });
    }

    // Status breakdown for radial
    const statusRadial = [
      { name: 'PR Approved', value: prApprovalRate, fill: '#10b981' },
      { name: 'PO Approved', value: poApprovalRate, fill: '#6366f1' },
      { name: 'Conversion', value: Math.min(conversionRate, 100), fill: '#f59e0b' },
    ];

    // Top departments from PRs
    const deptMap = {};
    prData.forEach(pr => {
      const dept = pr.department || pr.departmentName || 'Unknown';
      if (!deptMap[dept]) deptMap[dept] = { dept, PRs: 0, Approved: 0, Rejected: 0 };
      deptMap[dept].PRs++;
      if (pr.status === 2) deptMap[dept].Approved++;
      if (pr.status === 3) deptMap[dept].Rejected++;
    });
    const deptData = Object.values(deptMap).sort((a, b) => b.PRs - a.PRs).slice(0, 6);

    // Recent 7-day activity
    const activityDays = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const ds = d.toISOString().slice(0, 10);
      activityDays.push({
        day: d.toLocaleDateString('en-US', { weekday: 'short' }),
        PR: prData.filter(p => (p.creationDate || '').slice(0, 10) === ds).length,
        PO: poData.filter(p => (p.poCreatedDate || '').slice(0, 10) === ds).length,
      });
    }

    // Avg processing time (created → approved) in days
    const approvedPRs = prData.filter(p => p.status === 2 && p.creationDate && p.dateApproved);
    const avgPRDays = approvedPRs.length > 0
      ? (approvedPRs.reduce((sum, p) => sum + (new Date(p.dateApproved) - new Date(p.creationDate)) / (1000 * 60 * 60 * 24), 0) / approvedPRs.length).toFixed(1)
      : '—';

    return { prApprovalRate, poApprovalRate, conversionRate, months, weeks, statusRadial, deptData, activityDays, avgPRDays };
  }, [prData, poData]);

  const getMonthlyData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months.map((month, idx) => ({
      month,
      PR: prData.filter(pr => new Date(pr.creationDate).getMonth() === idx).length,
      PO: poData.filter(po => new Date(po.poCreatedDate).getMonth() === idx).length
    }));
  };

  const pieData = [
    { name: 'Draft', value: prStats.draft + poStats.draft, color: '#FCD34D' },
    { name: 'Pending', value: prStats.pending + poStats.pending, color: '#FB923C' },
    { name: 'Approved', value: prStats.approved + poStats.approved, color: '#34D399' },
    { name: 'Rejected', value: prStats.rejected + poStats.rejected, color: '#F87171' }
  ];

  const getTimeFilterLabel = () => ({ day: 'Today', '3days': 'Last 3 Days', week: 'This Week', month: 'This Month' }[ratingTimeFilter] || 'This Month');
  const getPOTimeFilterLabel = () => ({ day: 'Today', '3days': 'Last 3 Days', week: 'This Week', month: 'This Month' }[poRatingTimeFilter] || 'This Month');

  const invStats = {
    total: inventoryData.length,
    active: inventoryData.filter(i => i.status === 'Active').length,
    outOfStock: inventoryData.filter(i => i.stockStatus === 'Out of Stock').length,
    expiring: inventoryData.filter(i => i.expiryStatus !== 'Valid').length,
  };

  const TimeFilterButtons = ({ value, onChange, activeColor }) => (
    <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
      {[{ key: 'day', label: 'Day' }, { key: '3days', label: '3 Days' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }].map(({ key, label }) => (
        <button key={key} onClick={() => onChange(key)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${value === key ? `bg-white ${activeColor} shadow-sm` : 'text-gray-500 hover:text-gray-700'}`}>
          <Calendar className="w-3 h-3" />{label}
        </button>
      ))}
    </div>
  );

  const groupedInventory = inventoryData.reduce((acc, item) => {
    const existing = acc.find(i => i.itemCode === item.itemCode);
    if (existing) {
      existing.availableQty += item.availableQty;
      existing.reservedQty += (item.reservedQty ?? 0);
      if (item.stockStatus === 'Out of Stock') existing.stockStatus = 'Out of Stock';
      if (item.expiryStatus !== 'Valid') existing.expiryStatus = item.expiryStatus;
      if (new Date(item.lastUpdated) > new Date(existing.lastUpdated)) existing.lastUpdated = item.lastUpdated;
    } else {
      acc.push({ ...item, availableQty: item.availableQty, reservedQty: item.reservedQty ?? 0 });
    }
    return acc;
  }, []);

  // ─── DRAGGABLE WRAPPER ───────────────────────────────────────────────────────
  const DraggableWidget = ({ id, children, className = "" }) => (
    <div
      draggable
      onDragStart={(e) => handleDragStart(e, id)}
      onDragOver={handleDragOver}
      onDrop={(e) => handleDrop(e, id)}
      onDragEnd={handleDragEnd}
      className={`${className} ${draggedItem === id ? 'opacity-50 scale-[0.99]' : ''} transition-all duration-200 relative group`}
    >
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <div className="bg-gray-700/70 backdrop-blur-sm rounded-lg p-1.5 cursor-grab active:cursor-grabbing">
          <GripVertical className="w-4 h-4 text-white" />
        </div>
      </div>
      {children}
    </div>
  );

  // ─── KPI CARD COMPONENT ──────────────────────────────────────────────────────
  const KPICard = ({ label, value, suffix = '', icon: Icon, from, to, textColor, sub, trend, trendUp }) => (
    <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-xl transition-all duration-300 group cursor-default">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2.5 bg-gradient-to-br ${from} ${to} rounded-xl shadow group-hover:scale-110 transition-transform`}>
          <Icon className="text-white" size={18} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${trendUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
            {trendUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {trend}%
          </div>
        )}
      </div>
      <p className={`text-3xl font-black ${textColor} mb-0.5`}>{value}<span className="text-lg ml-1 font-bold opacity-60">{suffix}</span></p>
      <p className="text-xs text-gray-500 font-semibold">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );

  // ─── WIDGETS ────────────────────────────────────────────────────────────────
  const widgets = {

    // STATS
    'stats': (
      <DraggableWidget id="stats" key="stats">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { label: 'Total Documents', value: prStats.total + poStats.total, icon: Package, from: 'from-blue-500', to: 'to-blue-600', badge: 'text-blue-700 bg-blue-50', badgeLabel: 'Total' },
            { label: 'Awaiting Approval', value: prStats.pending + poStats.pending, icon: Clock, from: 'from-amber-500', to: 'to-orange-500', badge: 'text-orange-700 bg-orange-50', badgeLabel: 'Pending' },
            { label: 'Completed', value: prStats.approved + poStats.approved, icon: CheckCircle, from: 'from-emerald-500', to: 'to-green-500', badge: 'text-green-700 bg-green-50', badgeLabel: 'Approved' },
            { label: 'Disapproved', value: prStats.rejected + poStats.rejected, icon: XCircle, from: 'from-rose-500', to: 'to-red-500', badge: 'text-red-700 bg-red-50', badgeLabel: 'Rejected' },
          ].map(({ label, value, icon: Icon, from, to, badge, badgeLabel }, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group cursor-default">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2.5 bg-gradient-to-br ${from} ${to} rounded-xl shadow group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="text-white" size={20} />
                </div>
                <span className={`text-xs font-bold ${badge} px-2.5 py-1 rounded-full`}>{badgeLabel}</span>
              </div>
              <p className="text-3xl font-black text-gray-800 mb-0.5">{value}</p>
              <p className="text-xs text-gray-400 font-medium">{label}</p>
              <div className="mt-3 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full bg-gradient-to-r ${from} ${to} rounded-full w-full`} />
              </div>
            </div>
          ))}
        </div>
      </DraggableWidget>
    ),

    // ✅ NEW: ANALYTICS KPI ROW
    'analytics-kpi': (
      <DraggableWidget id="analytics-kpi" key="analytics-kpi">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <KPICard
            label="PR Approval Rate"
            value={analyticsData.prApprovalRate}
            suffix="%"
            icon={Target}
            from="from-emerald-500" to="to-green-600"
            textColor="text-emerald-600"
            sub={`${prStats.approved} of ${prStats.total} approved`}
            trendUp={analyticsData.prApprovalRate >= 50}
          />
          <KPICard
            label="PO Approval Rate"
            value={analyticsData.poApprovalRate}
            suffix="%"
            icon={CheckCircle}
            from="from-indigo-500" to="to-indigo-600"
            textColor="text-indigo-600"
            sub={`${poStats.approved} of ${poStats.total} approved`}
            trendUp={analyticsData.poApprovalRate >= 50}
          />
          <KPICard
            label="PR→PO Conversion"
            value={Math.min(analyticsData.conversionRate, 999)}
            suffix="%"
            icon={ArrowUpRight}
            from="from-amber-500" to="to-orange-500"
            textColor="text-amber-600"
            sub={`${poStats.total} POs from ${prStats.approved} approved PRs`}
            trendUp={analyticsData.conversionRate >= 50}
          />
          <KPICard
            label="Rejection Rate"
            value={prStats.total > 0 ? Math.round(((prStats.rejected + poStats.rejected) / (prStats.total + poStats.total)) * 100) : 0}
            suffix="%"
            icon={XCircle}
            from="from-rose-500" to="to-red-600"
            textColor="text-rose-600"
            sub={`${prStats.rejected + poStats.rejected} total rejected`}
            trendUp={false}
          />
        </div>
      </DraggableWidget>
    ),

    // TOP RATED COMBINED
    'top-rated-combined': (
      <DraggableWidget id="top-rated-combined" key="top-rated-combined">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* TOP RATED PRs */}
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow">
                  <ClipboardList className="text-white" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-black text-gray-800">Top 3 Rated PRs</h2>
                  <p className="text-xs text-gray-400">{topRatedPRs.length} rated • {getTimeFilterLabel()}</p>
                </div>
              </div>
              <TimeFilterButtons value={ratingTimeFilter} onChange={setRatingTimeFilter} activeColor="text-blue-600" />
            </div>
            {topRatedPRs.length === 0 ? (
              <div className="text-center py-10"><Star className="w-12 h-12 mx-auto mb-3 opacity-20 text-gray-300" /><p className="text-sm text-gray-400">No rated PRs for {getTimeFilterLabel().toLowerCase()}</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topRatedPRs.map((pr, index) => {
                  const styles = [
                    { gradient: 'from-blue-500 to-blue-600', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
                    { gradient: 'from-indigo-500 to-indigo-600', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
                    { gradient: 'from-violet-500 to-violet-600', bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' },
                  ];
                  const s = styles[index];
                  return (
                    <div key={pr.id} className={`rounded-xl p-4 border-2 ${s.border} bg-gradient-to-br from-gray-50 to-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className={`w-9 h-9 bg-gradient-to-br ${s.gradient} rounded-lg flex items-center justify-center shadow`}><ShoppingBag className="text-white" size={16} /></div>
                        <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < pr.rate ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />)}</div>
                      </div>
                      <span className={`${s.bg} ${s.text} text-xs font-bold px-2 py-0.5 rounded-md`}>#{index + 1} RANK</span>
                      <div className="mt-2 space-y-1 mb-3">
                        <p className="font-black text-gray-800 text-sm truncate">{pr.prNo}</p>
                        <p className="text-xs text-gray-500">{pr.prType || 'N/A'}</p>
                        <p className="text-xs text-gray-400">Created: {formatDate(pr.creationDate)}</p>
                        <p className="text-xs text-gray-400 line-clamp-2">{pr.remarks || 'No remarks'}</p>
                      </div>
                      <div className={`bg-gradient-to-r ${s.gradient} rounded-lg p-2.5 text-center`}>
                        <p className="text-xl font-black text-white">{pr.rate.toFixed(1)}</p>
                        <p className="text-xs text-white/70 font-semibold">RATING SCORE</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* TOP RATED POs */}
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow">
                  <ShoppingCart className="text-white" size={20} />
                </div>
                <div>
                  <h2 className="text-lg font-black text-gray-800">Top 3 Rated POs</h2>
                  <p className="text-xs text-gray-400">{topRatedPOs.length} rated • {getPOTimeFilterLabel()}</p>
                </div>
              </div>
              <TimeFilterButtons value={poRatingTimeFilter} onChange={setPoRatingTimeFilter} activeColor="text-purple-600" />
            </div>
            {topRatedPOs.length === 0 ? (
              <div className="text-center py-10"><Star className="w-12 h-12 mx-auto mb-3 opacity-20 text-gray-300" /><p className="text-sm text-gray-400">No rated POs for {getPOTimeFilterLabel().toLowerCase()}</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topRatedPOs.map((po, index) => {
                  const styles = [
                    { gradient: 'from-purple-500 to-purple-600', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
                    { gradient: 'from-pink-500 to-pink-600', bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
                    { gradient: 'from-rose-500 to-rose-600', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
                  ];
                  const s = styles[index];
                  return (
                    <div key={po.id} className={`rounded-xl p-4 border-2 ${s.border} bg-gradient-to-br from-gray-50 to-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className={`w-9 h-9 bg-gradient-to-br ${s.gradient} rounded-lg flex items-center justify-center shadow`}><ShoppingCart className="text-white" size={16} /></div>
                        <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < po.rate ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'}`} />)}</div>
                      </div>
                      <span className={`${s.bg} ${s.text} text-xs font-bold px-2 py-0.5 rounded-md`}>#{index + 1} RANK</span>
                      <div className="mt-2 space-y-1 mb-3">
                        <p className="font-black text-gray-800 text-sm truncate">{po.poNumber}</p>
                        <p className="text-xs text-gray-500">{po.paymentTerms || 'N/A'}</p>
                        <p className="text-xs text-gray-400">Created: {formatDate(po.poCreatedDate)}</p>
                        <p className="text-xs text-gray-400 line-clamp-2">{po.remarks || 'No remarks'}</p>
                      </div>
                      <div className={`bg-gradient-to-r ${s.gradient} rounded-lg p-2.5 text-center`}>
                        <p className="text-xl font-black text-white">{po.rate.toFixed(1)}</p>
                        <p className="text-xs text-white/70 font-semibold">RATING SCORE</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DraggableWidget>
    ),

    // CHARTS
    'charts': (
      <DraggableWidget id="charts" key="charts">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow"><BarChart3 className="text-white" size={18} /></div>
              <h3 className="text-base font-bold text-gray-800">Monthly Trends (Full Year)</h3>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={getMonthlyData()} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="PR" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                <Bar dataKey="PO" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow"><Activity className="text-white" size={18} /></div>
              <h3 className="text-base font-bold text-gray-800">Status Distribution</h3>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={90} dataKey="value">
                  {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </DraggableWidget>
    ),

    // ✅ NEW: ADVANCED ANALYTICS
    'analytics-advanced': (
      <DraggableWidget id="analytics-advanced" key="analytics-advanced">
        <div className="flex flex-col gap-5">

          {/* Row 1: Area trend + Department bar */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

            {/* 7-day activity area chart */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl shadow"><Zap className="text-white" size={18} /></div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">7-Day Activity</h3>
                  <p className="text-xs text-gray-400">PR & PO submissions this week</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={analyticsData.activityDays}>
                  <defs>
                    <linearGradient id="prGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="poGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="day" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Area type="monotone" dataKey="PR" stroke="#3b82f6" strokeWidth={2.5} fill="url(#prGrad)" dot={{ r: 4, fill: '#3b82f6' }} />
                  <Area type="monotone" dataKey="PO" stroke="#8b5cf6" strokeWidth={2.5} fill="url(#poGrad)" dot={{ r: 4, fill: '#8b5cf6' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Department breakdown */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl shadow"><Users className="text-white" size={18} /></div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">PRs by Department</h3>
                  <p className="text-xs text-gray-400">Top 6 departments</p>
                </div>
              </div>
              {analyticsData.deptData.length === 0 ? (
                <div className="flex items-center justify-center h-[220px] text-gray-300">
                  <div className="text-center"><Users size={32} className="mx-auto mb-2 opacity-30" /><p className="text-sm text-gray-400">No department data</p></div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={analyticsData.deptData} layout="vertical" margin={{ left: 60, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" stroke="#9ca3af" style={{ fontSize: '10px' }} allowDecimals={false} />
                    <YAxis type="category" dataKey="dept" stroke="#9ca3af" style={{ fontSize: '10px' }} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    <Bar dataKey="PRs" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={12} />
                    <Bar dataKey="Approved" fill="#10b981" radius={[0, 4, 4, 0]} barSize={12} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Row 2: Last 6 months line + Approval radial */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* 6-month line trend */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl shadow"><TrendingUp className="text-white" size={18} /></div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">6-Month Trend</h3>
                  <p className="text-xs text-gray-400">PR submissions & approvals over time</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={analyticsData.months}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '11px' }} />
                  <YAxis stroke="#9ca3af" style={{ fontSize: '11px' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="PR" stroke="#3b82f6" strokeWidth={2.5} dot={{ r: 4, fill: '#3b82f6' }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="PO" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 4, fill: '#8b5cf6' }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="Approved" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#10b981' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Approval rate radial */}
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow"><Target className="text-white" size={18} /></div>
                <div>
                  <h3 className="text-base font-bold text-gray-800">Performance</h3>
                  <p className="text-xs text-gray-400">Approval & conversion rates</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <RadialBarChart cx="50%" cy="50%" innerRadius="30%" outerRadius="90%"
                  data={analyticsData.statusRadial} startAngle={90} endAngle={-270}>
                  <RadialBar background dataKey="value" cornerRadius={6} />
                  <Tooltip content={<CustomTooltip />} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="mt-3 space-y-2">
                {analyticsData.statusRadial.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.fill }} />
                      <span className="text-gray-500 font-medium">{item.name}</span>
                    </div>
                    <span className="font-bold text-gray-800">{item.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DraggableWidget>
    ),

    // COMBINED SECTION
    'combined-section': (
      <DraggableWidget id="combined-section" key="combined-section">
        <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow"><FileText className="text-white" size={20} /></div>
            <h2 className="text-lg font-black text-gray-800">Purchase Request & Order Summary</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Draft', prValue: prStats.draft, poValue: poStats.draft, from: 'from-yellow-400', to: 'to-yellow-500', textColor: 'text-yellow-700', bg: 'bg-yellow-50', icon: FileText },
              { label: 'Pending', prValue: prStats.pending, poValue: poStats.pending, from: 'from-orange-400', to: 'to-orange-500', textColor: 'text-orange-700', bg: 'bg-orange-50', icon: Clock },
              { label: 'Approved', prValue: prStats.approved, poValue: poStats.approved, from: 'from-green-400', to: 'to-green-500', textColor: 'text-green-700', bg: 'bg-green-50', icon: CheckCircle },
              { label: 'Rejected', prValue: prStats.rejected, poValue: poStats.rejected, from: 'from-red-400', to: 'to-red-500', textColor: 'text-red-700', bg: 'bg-red-50', icon: XCircle },
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 border border-gray-200 hover:shadow-lg transition-all duration-300 group hover:-translate-y-0.5 cursor-default">
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 bg-gradient-to-br ${item.from} ${item.to} rounded-lg shadow group-hover:scale-110 transition-transform`}>
                    <item.icon className="text-white" size={16} />
                  </div>
                  <span className={`text-xs font-bold ${item.textColor} ${item.bg} px-2 py-0.5 rounded-full`}>{item.label}</span>
                </div>
                <div className="flex items-end gap-3 mb-2">
                  <div><p className={`text-2xl font-black ${item.textColor}`}>{item.prValue}</p><p className="text-xs text-blue-400 font-semibold">PR</p></div>
                  <div className="text-gray-300 text-xl font-light mb-3">/</div>
                  <div><p className={`text-2xl font-black ${item.textColor}`}>{item.poValue}</p><p className="text-xs text-purple-400 font-semibold">PO</p></div>
                </div>
                <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div className={`h-full bg-gradient-to-r ${item.from} ${item.to} rounded-full transition-all duration-500`}
                    style={{ width: `${(prStats.total + poStats.total) ? ((item.prValue + item.poValue) / (prStats.total + poStats.total)) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </DraggableWidget>
    ),

    // INVENTORY
    'inventory': canViewInventory ? (
      <DraggableWidget id="inventory" key="inventory">
        <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl shadow"><Boxes className="text-white" size={20} /></div>
            <div>
              <h2 className="text-lg font-black text-gray-800">Inventory Overview</h2>
              <p className="text-xs text-gray-400">{inventoryData.length} total items across all warehouses</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Items', value: invStats.total, icon: Boxes, from: 'from-teal-400', to: 'to-teal-500', textColor: 'text-teal-700', bg: 'bg-teal-50' },
              { label: 'Active', value: invStats.active, icon: CheckCircle, from: 'from-green-400', to: 'to-green-500', textColor: 'text-green-700', bg: 'bg-green-50' },
              { label: 'Out of Stock', value: invStats.outOfStock, icon: AlertTriangle, from: 'from-red-400', to: 'to-red-500', textColor: 'text-red-700', bg: 'bg-red-50' },
              { label: 'Expiring', value: invStats.expiring, icon: Clock, from: 'from-orange-400', to: 'to-orange-500', textColor: 'text-orange-700', bg: 'bg-orange-50' },
            ].map((item, idx) => (
              <div key={idx} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-300 group cursor-default">
                <div className="flex items-center justify-between mb-2">
                  <div className={`p-2 bg-gradient-to-br ${item.from} ${item.to} rounded-lg shadow group-hover:scale-110 transition-transform`}><item.icon className="text-white" size={15} /></div>
                  <span className={`text-xs font-bold ${item.textColor} ${item.bg} px-2 py-0.5 rounded-full`}>INV</span>
                </div>
                <p className={`text-2xl font-black ${item.textColor} mb-0.5`}>{item.value}</p>
                <p className="text-xs text-gray-500 font-medium">{item.label}</p>
              </div>
            ))}
          </div>
          <div className="overflow-x-auto rounded-xl border border-gray-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  {['Item Code', 'Description', 'Avail Qty', 'Reserved', 'UOM', 'Stock', 'Expiry', 'Last Updated'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {groupedInventory.slice(0, 15).map((item, idx) => (
                  <tr key={item.itemCode} className={`hover:bg-teal-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    <td className="px-4 py-3 font-bold text-teal-700 text-xs">{item.itemCode}</td>
                    <td className="px-4 py-3 text-gray-700 text-xs font-medium max-w-[200px] truncate">{item.itemDescription}</td>
                    <td className="px-4 py-3 text-right"><span className="font-black text-teal-600 text-sm">{item.availableQty}</span></td>
                    <td className="px-4 py-3 text-right"><span className="font-black text-gray-600 text-sm">{item.reservedQty}</span></td>
                    <td className="px-4 py-3 text-xs text-gray-500">{item.uom}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${item.stockStatus === 'Out of Stock' ? 'bg-red-100 text-red-700' : item.stockStatus === 'Low Stock' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>{item.stockStatus}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${item.expiryStatus === 'Valid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>{item.expiryStatus}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{formatDate(item.lastUpdated)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {groupedInventory.length > 15 && (
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-400">Showing <span className="font-bold text-gray-600">15</span> of <span className="font-bold text-teal-600">{groupedInventory.length}</span> items</p>
              </div>
            )}
            {inventoryData.length === 0 && (
              <div className="text-center py-12"><Boxes className="w-12 h-12 mx-auto mb-3 text-gray-200" /><p className="text-sm text-gray-400">No inventory data found</p></div>
            )}
          </div>
        </div>
      </DraggableWidget>
    ) : null,

    // QUICK ACTIONS
    'actions': (
      <DraggableWidget id="actions" key="actions">
        <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow"><AlertCircle className="text-white" size={20} /></div>
            <h2 className="text-lg font-black text-gray-800">Quick Actions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: User, label: 'Update Profile', gradient: 'from-blue-500 to-cyan-500' },
              { icon: Plus, label: 'Create New PR', gradient: 'from-purple-500 to-pink-500' },
              { icon: TrendingUp, label: 'Track Requests', gradient: 'from-indigo-500 to-purple-500' },
            ].map((item, idx) => (
              <button key={idx} className="group bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-200 hover:border-gray-300 transition-all hover:shadow-lg hover:-translate-y-0.5">
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${item.gradient} mb-3 group-hover:scale-110 transition-transform shadow`}><item.icon className="text-white" size={22} /></div>
                <p className="text-sm font-bold text-gray-700 group-hover:text-gray-900 transition-colors">{item.label}</p>
              </button>
            ))}
          </div>
        </div>
      </DraggableWidget>
    ),
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-sm text-gray-400 font-medium">Loading dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/50 to-indigo-50 p-6">
      <div className="max-w-screen-2xl mx-auto space-y-5">

        {/* Header */}
        <div className="bg-white rounded-2xl px-6 py-5 shadow-md border border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow">
                <Sparkles className="text-white" size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-black bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Procurement Dashboard
                </h1>
                <p className="text-xs text-gray-400 mt-0.5">
                  Real-time overview • Last refreshed: {lastRefreshed.toLocaleTimeString()}
                  {canViewInventory && ' • Includes Inventory'} • Drag widgets to reorder
                </p>
              </div>
            </div>
            <button onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl text-sm font-semibold transition-all border border-blue-100">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* Widgets */}
        {widgetOrder.map(widgetId => widgets[widgetId] || null)}

      </div>
    </div>
  );
}